import { Injectable, inject } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { catchError, Observable, of, switchMap } from "rxjs";
import { DataService } from "./data.service";
import { OrderService } from "./order.service";
import { TableAndRestaurant } from "../models/config.model";
import { Transaction } from "../models/payment.model";
import { ApiService } from "./api.service";
import { PaymentMode } from "../shared/enums";
import { ToastService } from "./toast.service";

@Injectable({ providedIn: "root" })
export class PaymentValidationService {
  private api = inject(ApiService);
  private orderService = inject(OrderService);
  private dataService = inject(DataService);
  private toastService = inject(ToastService);
  private translate = inject(TranslateService);

  public setTableAndRestaurant(queryString): Observable<TableAndRestaurant> {
    const tableId = this.getFromQueryString(queryString, "table_id");
    return this.api.getTableAndRestaurant(tableId);
  }
  /**
   * validateNi
   * This method is to validate the payment for Network International.
   * After a redirection, the query string will contain: table_id, transaction_id,
   * resultIndicator and sessionVersion.
   * We need to make an api call with transaction_id and resultIndicator to confirm
   * whether the matching order has been really paid. The api returns 200 in case
   * of success (no body) or a series of 400 errors.
   */
  validateNI(queryString: string): Observable<Transaction | null> {
    const transactionId = this.getFromQueryString(
      queryString,
      "transaction_id"
    );
    const refCode = this.getFromQueryString(queryString, "resultIndicator");
    const paymentMode = this.dataService.restaurant().payment_mode;
    if (transactionId === null || refCode === null) {
      return of(null);
    }

    // for adyen
    if (refCode === null || refCode === "") {
      if (paymentMode === PaymentMode.Prepaid) {
        this.orderService.setLastOrder();
        return of(null);
      }

      return this.orderService.setLastTransaction(transactionId);
    }

    const payload = { transaction_id: transactionId, ref_code: refCode };

    return this.api.validateNiTransaction(payload).pipe(
      switchMap(() => {
        if (paymentMode === PaymentMode.Prepaid) {
          this.orderService.setLastOrder();
          return of(null); // prepaid path doesn't return a transaction
        }
        // Postpaid: fetch and store the full transaction
        return this.orderService.setLastTransaction(transactionId);
      }),
      catchError(() => {
        this.orderService.setLastOrder();
        return of(null); // swallow the error, stream completes normally
      })
    );
  }

  /**
   * Keep original name for drop-in compatibility.
   * Handles ?payment_token=success|fail (+ optional transaction_id).
   */
  validatePayment$(queryString: string): Observable<Transaction | null> {
    const paymentToken = this.getFromQueryString(queryString, "payment_token");
    const transactionId = this.getFromQueryString(
      queryString,
      "transaction_id"
    );
    const paymentMode = this.dataService.restaurant().payment_mode;

    // No token provided → nothing to do
    if (paymentToken === null) {
      return of(null);
    }

    // Failure → show error and finish
    if (paymentToken === "fail") {
      this.toastService.error(this.translate.instant("PAYMENT.ERROR"));
      return of(null);
    }

    // Success path
    if (paymentToken === "success") {
      // Prepaid confirms via /confirm-order; Postpaid and Pay-at-table both stay on
      // this page and show the transaction (mirrors validateNI's mode split above).
      if (paymentMode === PaymentMode.Prepaid) {
        this.orderService.setLastOrder();
      }

      if (transactionId) {
        return this.orderService.setLastTransaction(transactionId);
      }

      // Missing transaction id on a non-Prepaid success → nothing to fetch
      return of(null);
    }

    // Any other token value → ignore
    return of(null);
  }

  /** --- helpers --- */
  public getFromQueryString(queryString: string, key: string): string {
    // We remove the '?' from the query string
    const query: string = queryString.substr(1);

    // We get the individual parameters
    const splitted: string[] = query.split("&");

    // Searching for the index where the key is present
    const index = splitted.findIndex((elem) => elem.startsWith(key));

    // index is -1, nothing found
    if (index === -1) {
      return "";
    }

    // We retrieve the couple "key=value"
    const couple = splitted[index];

    // And then we return the value (key [0] and value [1])
    return couple.split("=")[1];
  }
}
